import { useSelector } from "react-redux";
import { useMemo, useRef } from "react";
import { useGetUserMascots } from "queries/useGetUserMascots";
import { useGetLoyaltyUser } from "queries/useGetLoyaltyUser";
import { useGetProgressLoyalty } from "queries/useGetProgressLoyalty";
import * as userSelectors from "redux/user/selectors";
import { useGetBoostersUser } from "queries/useGetBoostersUser";
import { useGetRewardsInfo } from "queries/useGetRewardsInfo";
import { useGetGamesByIds } from "queries/useGetGamesByIds";
import { useGetLoyaltyRewardsUser } from "queries/useGetLoyaltyRewardsUser";
import { useInfoBoosters } from "queries/useInfoBoosters";
import { getIdsRewards, getRewardsOnRank } from "utils/loyalty-rewards";
import { getInfoRank } from "utils/getInfoRank";

export const useFormatCardsReward = (
  nameRank?: string,
  level?: string | number,
  canLoadInfo?: boolean,
) => {
  const { data: dataLoyalty, isLoading: isLoadingLoyalty } =
    useGetLoyaltyUser();
  const loyalty = dataLoyalty?.data?.data;
  const { userRewards, isLoading: isLoadingRewards } = useGetLoyaltyRewardsUser(
    {
      loyaltyId: loyalty?.id,
    },
  );
  const { data: dataProgress, isLoading: isLoadingProgress } =
    useGetProgressLoyalty();
  const progress = dataProgress?.data?.data;
  const userExperience = progress?.experience || 0;
  const currency = useSelector(userSelectors.currency);
  const refInfoRank = useRef<{ nameRank: string; level?: number } | undefined>(
    nameRank ? { nameRank, level: Number(level) } : undefined,
  );

  const {
    nominals,
    freeSpins,
    boosters,
    cashbackDaily,
    cashbackWeekly,
    cashbackMonthly,
  } = useMemo(
    () => getIdsRewards(loyalty, currency?.isoCode, refInfoRank.current),
    [loyalty, currency?.isoCode],
  );

  const { boosters: boostersUser, isLoading: isLoadingBoosters } =
    useGetBoostersUser();

  const { rewardsInfo, isLoading: isLoadingInfoRewards } = useGetRewardsInfo(
    [
      ...nominals,
      ...freeSpins,
      ...cashbackDaily,
      ...cashbackWeekly,
      ...cashbackMonthly,
    ],
    canLoadInfo,
  );

  const idsGamesFreeSpins = rewardsInfo?.reduce<string[]>(
    (acc, cur) =>
      "rewardFreeSpin" in cur && cur?.rewardFreeSpin?.freeSpinsGame
        ? [...acc, cur.rewardFreeSpin.freeSpinsGame]
        : acc,
    [],
  );
  const idsGamesBoosters = boostersUser.map((booster) => booster.gameId);
  const { games, isLoading: isLoadingGames } = useGetGamesByIds(
    [...new Set([...idsGamesFreeSpins, ...idsGamesBoosters])],
    canLoadInfo,
  );

  const { data: dataMascots } = useGetUserMascots();
  const boostersInfo = useInfoBoosters(boosters, canLoadInfo);

  const cards = useMemo(
    () =>
      getRewardsOnRank({
        nameRank,
        loyalty,
        userRewards,
        userExperience,
        infoRewards: rewardsInfo,
        currencyCode: currency?.isoCode ? currency?.isoCode : "",
        gamesInfo: games,
        boostersUser,
        boostersInfo,
        mascots: dataMascots?.data?.data || [],
        indexLevel: level ? Number(level) - 1 : undefined,
      }),
    [
      level,
      nameRank,
      loyalty,
      userRewards,
      userExperience,
      rewardsInfo,
      currency?.isoCode,
      games,
      boostersUser,
      boostersInfo,
      dataMascots?.data.data,
    ],
  );

  const data = useMemo(
    () => getInfoRank(nameRank, cards, loyalty, level),
    [cards, loyalty, nameRank, level],
  );

  return {
    cards,
    ...data,
    isLoading:
      isLoadingGames ||
      isLoadingRewards ||
      isLoadingInfoRewards ||
      isLoadingProgress ||
      isLoadingLoyalty ||
      isLoadingBoosters ||
      canLoadInfo === false,
    isPrev:
      progress &&
      nameRank === loyalty?.settings?.ranks?.[progress?.rank]?.name &&
      level &&
      Number(level) - 1 === progress?.level,
  };
};
